import React, { Fragment, useEffect } from 'react';
import { useTrackableLink } from '@noths/polaris-client-react-hooks';
import { visuallyHidden } from '@noths/polaris-client-styles';

import type { SearchSuggestion } from './types';
import * as styles from './SearchSuggestions.styles';
import { useSearchContext } from 'client-site-chrome-ui/context/SearchContext/useSearchContext';
import { useSiteChromeDynamicContext } from 'client-site-chrome-ui/context/SiteChromeDynamicContext/useSiteChromeDynamicContext';
import { createSuggestionSearchEvent } from 'client-site-chrome-ui/hooks/useSearch/tracking';

interface SuggestionProps {
  currentSearchTerm: string;
  isSelected: boolean;
  suggestion: SearchSuggestion;
}

const Suggestion = ({ currentSearchTerm, isSelected, suggestion }: SuggestionProps) => {
  const { trackLinkClick, trackLinkMouseDown } = useTrackableLink();
  const trackingEvent = createSuggestionSearchEvent(currentSearchTerm, suggestion);

  return (
    <li aria-selected={isSelected} css={styles.item} key={suggestion.name} role="option">
      <a
        css={[styles.link, isSelected && styles.itemSelected]}
        href={suggestion.href}
        onClick={trackLinkClick(trackingEvent)}
        onMouseDown={trackLinkMouseDown(trackingEvent)}
        tabIndex={0} /* required for e.relatedTarget to not be null in Safari  */
      >
        {suggestion.name}
      </a>
    </li>
  );
};

export const SearchSuggestions = () => {
  const {
    contentOverlay: { close, open },
  } = useSiteChromeDynamicContext();
  const {
    search: { isSuggestionsShown, searchTerm, selectedSuggestionId, suggestions, suggestionsCount },
    searchSuggestionsRef,
  } = useSearchContext();

  useEffect(() => {
    if (isSuggestionsShown) {
      open(searchSuggestionsRef.current?.getBoundingClientRect().bottom);
    } else {
      close();
    }

    return () => {
      close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, isSuggestionsShown]);

  const hiddenInstructionalText = () => {
    const multipleSuggestionsAvailable = suggestionsCount > 1;

    return `${suggestionsCount} result${multipleSuggestionsAvailable ? 's' : ''} ${
      multipleSuggestionsAvailable ? 'are' : 'is'
    } available, use up and down arrow keys to navigate`;
  };

  return (
    <div css={styles.container} data-testid="search-suggestion" ref={searchSuggestionsRef}>
      {suggestions.map((group, i) => (
        <Fragment key={i}>
          {group.title && <h3 css={styles.title}>{group.title}</h3>}
          <ul aria-label="Suggestions" css={styles.list} id="suggestionList" role="listbox">
            {group.suggestions.map((suggestion: SearchSuggestion) => (
              <Suggestion
                currentSearchTerm={searchTerm}
                isSelected={selectedSuggestionId === suggestion.globalIndex}
                key={suggestion.name}
                suggestion={suggestion}
              />
            ))}
          </ul>
        </Fragment>
      ))}
      <div aria-live="polite" css={visuallyHidden} role="status">
        {hiddenInstructionalText()}
      </div>
    </div>
  );
};
